import {confirmedSentMessageId} from '@lib/messarioAi/sentMessageId';
import {applyMessarioTemplateEffects, MessarioTemplatesFetchFailure} from '@lib/messarioTemplates/api';
import {
  clearMessarioTemplateEffectsPending,
  getMessarioTemplateEffectsPending,
  messarioTemplateEffectsFingerprintsMatch,
  MessarioTemplateEffectsPending
} from '@lib/messarioTemplates/templateEffectsIntent';
import {getMessarioTemplatesAccess} from '@lib/messarioTemplates/storage';
import {NULL_PEER_ID} from '@appManagers/constants';
import rootScope from '@lib/rootScope';

const APPLY_RETRY_BACKOFF_MS = [200, 500] as const;
const APPLY_MAX_ATTEMPTS = 1 + APPLY_RETRY_BACKOFF_MS.length;
const UNRESOLVED_FLUSH_MS = 30_000;

export type MessarioTemplateEffectsCompletionTarget = {
  template_id: number,
  template_fingerprint: string,
  peer_id: string,
  fingerprint: string,
  access_token: string,
  panel_origin: string
};

export type MessarioTemplateEffectsSendSuccessArgs = {
  peerId: PeerId,
  finalReplyText: string,
  sentMessageId?: string | number | null
};

/** Immutable post-send apply target until Panel confirms a terminal apply status. */
export type MessarioTemplateEffectsUnresolvedApply = {
  template_id: number,
  template_fingerprint: string,
  peer_id: string,
  sent_message_id: string,
  sent_text: string,
  access_token: string,
  panel_origin: string
};

const unresolvedAppliesByKey = new Map<string, MessarioTemplateEffectsUnresolvedApply>();
let unresolvedFlushTimer: ReturnType<typeof setInterval> | null = null;
let visibilityBound = false;

function currentExternalUserId(): string | undefined {
  if(!rootScope.myId || rootScope.myId === NULL_PEER_ID) {
    return undefined;
  }

  return String(rootScope.myId);
}

function panelOrigin(): string {
  return import.meta.env.VITE_MESSARIO_PANEL_ORIGIN || '';
}

function peerKeyFromPeerId(peerId: PeerId): string | null {
  if(!peerId || typeof peerId.isUser !== 'function' || !peerId.isUser()) {
    return null;
  }
  return String(peerId.toUserId());
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function unresolvedKey(entry: Pick<MessarioTemplateEffectsUnresolvedApply, 'peer_id' | 'sent_message_id'>): string {
  return `${entry.peer_id}:${entry.sent_message_id}`;
}

function registerUnresolvedApply(entry: MessarioTemplateEffectsUnresolvedApply) {
  unresolvedAppliesByKey.set(unresolvedKey(entry), entry);
  ensureUnresolvedFlushBindings();
}

function removeUnresolvedApply(entry: Pick<MessarioTemplateEffectsUnresolvedApply, 'peer_id' | 'sent_message_id'>) {
  unresolvedAppliesByKey.delete(unresolvedKey(entry));
}

function listUnresolvedForPeer(peerId: string): MessarioTemplateEffectsUnresolvedApply[] {
  const peerKey = String(peerId);
  return [...unresolvedAppliesByKey.values()].filter((entry) => entry.peer_id === peerKey);
}

/** Test helper — clears unresolved apply map. */
export function resetUnresolvedTemplateEffectsAppliesForTests() {
  unresolvedAppliesByKey.clear();
  if(unresolvedFlushTimer != null) {
    clearInterval(unresolvedFlushTimer);
    unresolvedFlushTimer = null;
  }
}

export function listUnresolvedTemplateEffectsAppliesForTests(): MessarioTemplateEffectsUnresolvedApply[] {
  return [...unresolvedAppliesByKey.values()];
}

export function isRetryableTemplateEffectsApplyError(error: unknown): boolean {
  if(error instanceof MessarioTemplatesFetchFailure) {
    if(error.kind === 'network') return true;
    if(error.kind === 'configuration') return false;
    const status = error.status;
    if(status == null) return true;
    if(status >= 500) return true;
    if(status === 408 || status === 429) return true;
    if(status >= 400 && status < 500) return false;
    return true;
  }
  if(error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as {status?: number}).status);
    if(Number.isFinite(status)) {
      if(status >= 500) return true;
      if(status === 408 || status === 429) return true;
      if(status >= 400 && status < 500) return false;
    }
  }
  return true;
}

async function applyMessarioTemplateEffectsWithRetries(
  entry: MessarioTemplateEffectsUnresolvedApply
): Promise<void> {
  let lastError: unknown;
  for(let attempt = 0; attempt < APPLY_MAX_ATTEMPTS; attempt++) {
    try {
      await applyMessarioTemplateEffects(
        entry.access_token,
        entry.panel_origin,
        {
          template_id: entry.template_id,
          template_fingerprint: entry.template_fingerprint,
          peer_id: entry.peer_id,
          sent_message_id: entry.sent_message_id,
          sent_text: entry.sent_text
        }
      );
      return;
    } catch(err) {
      lastError = err;
      if(!isRetryableTemplateEffectsApplyError(err)) {
        throw err;
      }
      if(attempt < APPLY_RETRY_BACKOFF_MS.length) {
        await sleep(APPLY_RETRY_BACKOFF_MS[attempt]);
      }
    }
  }

  throw lastError;
}

/**
 * Create synthetic Telegram mid once at capture-after-success (stable across retries).
 */
export function resolveMessarioTemplateEffectsSentMessageId(
  args: MessarioTemplateEffectsSendSuccessArgs,
  target: MessarioTemplateEffectsCompletionTarget
): string {
  if(args.sentMessageId != null && String(args.sentMessageId) !== '') {
    return String(args.sentMessageId);
  }

  return '';
}

/**
 * Capture immutable apply target from CURRENT pending + auth at Send start.
 */
export function captureMessarioTemplateEffectsTarget(
  peerId: PeerId
): MessarioTemplateEffectsCompletionTarget | null {
  const userId = currentExternalUserId();
  if(!userId) {
    return null;
  }

  const access = getMessarioTemplatesAccess(userId);
  if(!access?.token) {
    return null;
  }

  const peerKey = peerKeyFromPeerId(peerId);
  if(!peerKey) {
    return null;
  }

  const pending = getMessarioTemplateEffectsPending();
  if(!pending || pending.peer_id !== peerKey) {
    return null;
  }

  return {
    template_id: pending.template_id,
    template_fingerprint: pending.template_fingerprint,
    peer_id: pending.peer_id,
    fingerprint: pending.fingerprint,
    access_token: access.token,
    panel_origin: panelOrigin()
  };
}

/**
 * Call only after Telegram send Promise resolves.
 * Fingerprint mismatch / missing text → clear intent, no API / no unresolved.
 * On network/5xx: keep unresolved with stable sent_message_id for retry.
 */
export async function noteMessarioTemplateEffectsSendSuccess(
  args: MessarioTemplateEffectsSendSuccessArgs,
  target: MessarioTemplateEffectsCompletionTarget
): Promise<void> {
  const sentText = String(args.finalReplyText ?? '');
  if(!messarioTemplateEffectsFingerprintsMatch(sentText, target.fingerprint)) {
    clearMessarioTemplateEffectsPending();
    return;
  }

  // Capture immutable post-send target once; clear pending so another insert can proceed.
  const sentMessageId = resolveMessarioTemplateEffectsSentMessageId(args, target);
  if(!sentMessageId) return;
  clearMessarioTemplateEffectsPending();

  const unresolved: MessarioTemplateEffectsUnresolvedApply = {
    template_id: target.template_id,
    template_fingerprint: target.template_fingerprint,
    peer_id: target.peer_id,
    sent_message_id: sentMessageId,
    sent_text: sentText,
    access_token: target.access_token,
    panel_origin: target.panel_origin
  };
  registerUnresolvedApply(unresolved);

  try {
    await applyMessarioTemplateEffectsWithRetries(unresolved);
    removeUnresolvedApply(unresolved);
  } catch(err) {
    if(!isRetryableTemplateEffectsApplyError(err)) {
      removeUnresolvedApply(unresolved);
    }
    console.warn('Messario template-effects apply failed', err);
    throw err;
  }
}

/**
 * Retry Panel apply for unresolved confirmed sends (same sent_message_id).
 * Does NOT resend via Telegram.
 */
export async function retryUnresolvedMessarioTemplateEffectsAppliesForPeer(
  peerId: string | number
): Promise<void> {
  const items = listUnresolvedForPeer(String(peerId));
  if(!items.length) {
    return;
  }

  for(const item of items) {
    try {
      await applyMessarioTemplateEffectsWithRetries(item);
      removeUnresolvedApply(item);
    } catch(err) {
      if(!isRetryableTemplateEffectsApplyError(err)) {
        removeUnresolvedApply(item);
      }
      console.warn('Messario template-effects unresolved apply retry failed', err);
    }
  }
}

export async function flushUnresolvedMessarioTemplateEffectsApplies(): Promise<void> {
  const items = [...unresolvedAppliesByKey.values()];
  if(!items.length) {
    return;
  }

  for(const item of items) {
    try {
      await applyMessarioTemplateEffectsWithRetries(item);
      removeUnresolvedApply(item);
    } catch(err) {
      if(!isRetryableTemplateEffectsApplyError(err)) {
        removeUnresolvedApply(item);
      }
      console.warn('Messario template-effects unresolved flush failed', err);
    }
  }
}

function ensureUnresolvedFlushBindings() {
  if(typeof document !== 'undefined' && !visibilityBound) {
    visibilityBound = true;
    document.addEventListener('visibilitychange', () => {
      if(document.visibilityState === 'visible') {
        void flushUnresolvedMessarioTemplateEffectsApplies();
      }
    });
  }

  if(unresolvedFlushTimer == null && typeof setInterval === 'function') {
    unresolvedFlushTimer = setInterval(() => {
      if(unresolvedAppliesByKey.size === 0) return;
      void flushUnresolvedMessarioTemplateEffectsApplies();
    }, UNRESOLVED_FLUSH_MS);
  }
}

/**
 * Fire-and-continue next to watchMessarioAiSendCompletion.
 * On Telegram reject: clear intent (failed send → no effects).
 * On each send-complete tick: also flush prior unresolved for this peer.
 */
export function watchMessarioTemplateEffectsSendCompletion(
  sendCompletion: Promise<unknown>,
  args: MessarioTemplateEffectsSendSuccessArgs
): Promise<void> {
  const peerKey = peerKeyFromPeerId(args.peerId);
  if(peerKey) {
    void retryUnresolvedMessarioTemplateEffectsAppliesForPeer(peerKey);
  }

  const target = captureMessarioTemplateEffectsTarget(args.peerId);
  if(!target) {
    return sendCompletion.then(
      (): undefined => undefined,
      (): undefined => undefined
    );
  }

  return sendCompletion.then(
    (receipt): Promise<void> => noteMessarioTemplateEffectsSendSuccess({...args, sentMessageId: args.sentMessageId ?? confirmedSentMessageId(receipt)}, target),
    (): void => {
      clearMessarioTemplateEffectsPending();
    }
  ).then(
    (): undefined => undefined,
    (err: unknown): void => {
      console.warn('Messario template-effects sendCompletion note failed', err);
    }
  );
}

/** Test helper — expose pending shape for assertions. */
export function peekCapturedPendingForTests(): MessarioTemplateEffectsPending | null {
  return getMessarioTemplateEffectsPending();
}
